export const COMPARATORS: { code: string; label: string }[] = [
  { code: 'EQ', label: 'is' },
  { code: 'NEQ', label: 'is not' },
  { code: 'GT', label: 'greater than' },
  { code: 'LT', label: 'less than' },
  { code: 'CONTAINS', label: 'contains' },
];

/** Fallback state colour when the backend provides none. */
export const DEFAULT_STATE_COLOUR = '#98a2b3';

/**
 * State colour class → hex. The application-states API returns `stateColor` as a
 * named class (e.g. "primary", "warning", "yellow", "purple"), not a hex. These
 * values mirror the officer portal's `.irembo-badge.<class>` palette (the saturated
 * tone) so a state shows the SAME colour the officer portal renders it with.
 * Source: @irembo-andela/irembogov3-common `_globalVariables.scss` + officer portal styles.
 */
const STATE_COLOUR_HEX: Record<string, string> = {
  primary: '#0063cf', // --primary-color-600
  secondary: '#667085',
  success: '#0f996d', // $irembo-greentone-dark
  warning: '#f9772f', // $irembo-orangetone-dark
  yellow: '#dea700', // .irembo-badge.yellow
  danger: '#de1c3f', // $irembo-redtone-dark
  purple: '#4b46d7', // $irembo-purpletone-dark
  info: '#0063cf',
  light: '#98a2b3',
  dark: '#1d2939',
};

/**
 * Resolve a backend state colour to a CSS colour usable as-is in the builder.
 * Accepts a named class ("primary", "yellow", "purple"…), a hex/rgb/hsl string
 * (used verbatim), or null/empty (falls back to the default grey).
 */
export function resolveStateColour(raw: unknown): string {
  if (raw == null) return DEFAULT_STATE_COLOUR;
  const v = String(raw).trim();
  if (!v) return DEFAULT_STATE_COLOUR;
  if (v.startsWith('#') || /^(rgb|hsl)/i.test(v)) return v;
  return STATE_COLOUR_HEX[v.toLowerCase()] ?? DEFAULT_STATE_COLOUR;
}

/** Stable offline colour when the application-state catalog is unavailable. */
export function fallbackStateColour(stateCode: string): string {
  const code = String(stateCode || '').toUpperCase();
  if (/APPROV|COMPLET|SUCCESS|ACTIVE/.test(code)) return '#0f996d';
  if (/REJECT|CANCEL|FAIL|EXPIRE|DECLIN/.test(code)) return '#de1c3f';
  if (/PAYMENT|PAY_/.test(code)) return '#f9772f';
  if (/PENDING|WAITING|REVIEW/.test(code)) return '#dea700';
  if (/RESUBMIT|SUBMIT|PROCESS/.test(code)) return '#4b46d7';
  if (/^NEW$|DRAFT|INITIAL/.test(code)) return '#0063cf';
  const palette = ['#0063cf', '#0f996d', '#f9772f', '#4b46d7', '#087e8b'];
  const hash = [...code].reduce((value, char) => value + char.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

// Sourced from EApplicationEvent.java (application-mgmt-engine) — 19 constants
export const EVENTS: string[] = [
  'APPLY',
  'PAY',
  'INITIATE_PUSH',
  'ASSIGN_OFFICE',
  'PUSH',
  'APPROVE',
  'REJECT',
  'ESCALATE',
  'SET_APPOINTMENT',
  'REQUEST_FOR_ACTION',
  'EXPIRE_PAYMENT',
  'SUBMIT_RFA',
  'APPROVE_WITH_FORM',
  'TRANSITION',
  'CANCEL',
  'PROCESS_BULK_APPLICATION',
  'VALIDATE_AND_RECORD_STATUS',
  'NOTIFY_MASTER_CHILD_READY',
  'PROCESS_PAYMENT',
];

export interface IActionMeta {
  type: string;
  plain: string;
  icon: string;
  cls: string;
  description: string;
  usage: string;
}

type ActionMetaBase = Omit<IActionMeta, 'description' | 'usage'>;

const ACTION_HELP: Record<
  string,
  Pick<IActionMeta, 'description' | 'usage'>
> = {
  NOTIFICATION: {
    description:
      'Sends an applicant or internal workflow message when this state is reached.',
    usage:
      'Choose an API template when templates are managed in Notification Engine, or static content when this workflow owns the message body.',
  },
  GENERATE_CERTIFICATE: {
    description:
      'Creates a certificate or document from a configured document template.',
    usage:
      'Use after approval or completion states where the applicant should receive an official generated document.',
  },
  REGISTER_EXTERNAL_CERTIFICATE: {
    description:
      'Registers a certificate file that was already provided by an external partner or uploaded in the application.',
    usage:
      'Use when the workflow should save an existing attachment as the applicant certificate instead of generating a new document template.',
  },
  BILL_ID_GENERATION: {
    description:
      'Creates a bill reference that can be paid before the workflow continues.',
    usage:
      'Use when the next step requires payment and the billing metadata is known from the application or pricing result.',
  },
  EXECUTE_PRICING: {
    description:
      'Calculates the fee for the application using the configured pricing rules.',
    usage:
      'Use before bill generation when price depends on submitted fields, service rules, or selected options.',
  },
  OFFICE_ASSIGNMENT: {
    description:
      'Assigns the application to the office or processing unit responsible for the next state.',
    usage:
      'Use when officers must process the application from a fixed office, applicant location, form field, or expression.',
  },
  INTEGRATION: {
    description:
      'Calls a configured integration endpoint to exchange data with another system.',
    usage:
      'Use when this transition must validate, fetch, submit, or synchronize information with an external service.',
  },
  URL_CALL: {
    description:
      'Calls a configured URL-style endpoint as part of the workflow transition.',
    usage:
      'Use for lightweight backend callbacks where the endpoint code and retained response parameters drive later steps.',
  },
  FETCH_ID_PHOTO: {
    description:
      'Fetches an identity photo and writes the configured response value back to the application context.',
    usage:
      'Use when later review or document generation needs the applicant identity photo from an integration endpoint.',
  },
  INTERNAL_NOTIFICATION: {
    description:
      'Sends a notification to internal recipients instead of the applicant.',
    usage:
      'Use for operational alerts to staff emails or phone numbers when a state requires internal attention.',
  },
  FORM_FIELD_NOTIFICATION: {
    description:
      'Sends a notification to recipients captured in application form fields.',
    usage:
      'Use when recipient emails or phone numbers are dynamic and come from submitted form-field values.',
  },
  UPDATE_DATASET: {
    description:
      'Updates a dataset record using values from the workflow or application context.',
    usage:
      'Use when the transition should write application data into a service dataset for later lookup or reporting.',
  },
  TRIGGER_PARENT_SERVICE_EVENT: {
    description:
      'Triggers an event on the parent service application from this workflow.',
    usage:
      'Use in parent-child service flows when a child application state should move the parent workflow forward.',
  },
  TRIGGER_CHILD_SERVICE_EVENT: {
    description:
      'Triggers an event on a child service application from this workflow.',
    usage:
      'Use in parent-child service flows when the parent workflow should move a child application forward.',
  },
};

const actionHelp = (
  action: ActionMetaBase
): Pick<IActionMeta, 'description' | 'usage'> =>
  ACTION_HELP[action.type] || {
    description: `Runs the ${action.plain} workflow action when this state is reached.`,
    usage:
      'Use this action when the transition needs this backend workflow operation before the application moves on.',
  };

// Union of EActionType.java (application-mgmt-engine, 28 constants) and
// EWorkflowActionType.java (service-mgmt-engine, 27 constants).
// EActionType adds RESEND_NOTIFICATION which is absent from EWorkflowActionType.
// Total union: 28 unique action types.
export const ACTION_META: IActionMeta[] = (
  [
    {
      type: 'NOTIFICATION',
      plain: 'Send notification',
      icon: 'fa-bell',
      cls: 'msg',
    },
    {
      type: 'GENERATE_CERTIFICATE',
      plain: 'Issue a certificate',
      icon: 'fa-certificate',
      cls: 'cert',
    },
    {
      type: 'REGISTER_EXTERNAL_CERTIFICATE',
      plain: 'Register external certificate',
      icon: 'fa-file-circle-check',
      cls: 'cert',
    },
    {
      type: 'BILL_ID_GENERATION',
      plain: 'Create a payment bill',
      icon: 'fa-receipt',
      cls: 'bill',
    },
    {
      type: 'EXECUTE_PRICING',
      plain: 'Calculate the fee',
      icon: 'fa-money-bill',
      cls: 'bill',
    },
    {
      type: 'OFFICE_ASSIGNMENT',
      plain: 'Assign office',
      icon: 'fa-building',
      cls: 'office',
    },
    {
      type: 'INTEGRATION',
      plain: 'Integrate with another system',
      icon: 'fa-plug',
      cls: 'sys',
    },
    {
      type: 'RESEND_NOTIFICATION',
      plain: 'Resend Notification',
      icon: 'fa-gear',
      cls: 'sys',
    },
    { type: 'URL_CALL', plain: 'URL Call', icon: 'fa-gear', cls: 'sys' },
    {
      type: 'FETCH_ID_PHOTO',
      plain: 'Fetch ID Photo',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'SLOT_BOOKING',
      plain: 'Slot Booking',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'SLOT_REBOOKING',
      plain: 'Slot Rebooking',
      icon: 'fa-gear',
      cls: 'sys',
    },
    { type: 'REDEEM_SLOT', plain: 'Redeem Slot', icon: 'fa-gear', cls: 'sys' },
    {
      type: 'INTERNAL_NOTIFICATION',
      plain: 'Internal Notification',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'FORM_FIELD_NOTIFICATION',
      plain: 'Form Field Notification',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'INTEGRATION_UPDATE',
      plain: 'Integration Update',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'APPLY_SERVICE',
      plain: 'Apply Service',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'TRIGGER_PARENT',
      plain: 'Trigger Parent',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'SERVICE_CENTER_MASTER_BILL_ID_GENERATION',
      plain: 'Service Center Master Bill ID Generation',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'OFFICE_ASSIGNMENT_NOTIFICATION',
      plain: 'Office Assignment Notification',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'APPLY_NORMAL_SERVICE',
      plain: 'Apply Normal Service',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'UPDATE_DATASET',
      plain: 'Update Dataset',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'SYSTEM_REQUEST_FOR_ACTION',
      plain: 'System Request For Action',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'CREATE_CHILD_APPLICATION',
      plain: 'Create Child Application',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'MASTER_BILL_ID_GENERATION',
      plain: 'Master Bill ID Generation',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'TRIGGER_PARENT_SERVICE_EVENT',
      plain: 'Trigger Parent Service Event',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'TRIGGER_CHILD_SERVICE_EVENT',
      plain: 'Trigger Child Service Event',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'APPLY_OR_RESUBMIT_CHILD_APPLICATION',
      plain: 'Apply Or Resubmit Child Application',
      icon: 'fa-gear',
      cls: 'sys',
    },
    {
      type: 'VALIDATE_UNIQUE_APPLICATION_PROPERTY',
      plain: 'Validate Unique Application Property',
      icon: 'fa-gear',
      cls: 'sys',
    },
  ] as ActionMetaBase[]
).map(action => ({ ...action, ...actionHelp(action) }));

export const ACTION_BY_TYPE: Record<string, IActionMeta> = Object.fromEntries(
  ACTION_META.map(a => [a.type, a])
);
